/* =================================================================
   XENA FIT FINDER — Main JS (v2)
   2026-05-31
   FIXES v2:
     - Width-availability check (no more recommending non-existent size+W)
     - Per-style profile in localStorage
     - Customer metafield READ fallback (logged-in only)
     - Step-level Klaviyo telemetry
     - Klaviyo Added-to-Cart enrichment
     - Modal focus trap
   ================================================================= */
(function () {
  'use strict';

  var STORAGE_KEY_PREFIX = 'xena_fit_profile_';
  var SESSION_ID_KEY = 'xena_ff_session';
  var FF_VERSION = 'v1.3';
  var KLAVIYO_THROTTLE_MS = 30000;
  var lastKlaviyoEvent = 0;
  var ALL_SIZES = [5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10, 10.5, 11, 12, 13];

  // ========== RECOMMENDATION ENGINE (v2 — width-aware) ==========
  function clampToAvailableSizes(size, availableSizes) {
    if (!availableSizes || availableSizes.length === 0) {
      return { size: size, was_clamped: true, reason: 'empty_pool' };
    }
    var sorted = availableSizes.slice().sort(function (a, b) { return a - b; });
    var min = sorted[0], max = sorted[sorted.length - 1];
    if (size < min) return { size: min, was_clamped: true, reason: 'min_size' };
    if (size > max) return { size: max, was_clamped: true, reason: 'max_size' };
    if (sorted.indexOf(size) !== -1) return { size: size, was_clamped: false, reason: null };
    for (var i = 0; i < sorted.length; i++) {
      if (sorted[i] > size) return { size: sorted[i], was_clamped: true, reason: 'no_half_size' };
    }
    return { size: max, was_clamped: true, reason: 'max_size' };
  }

  function computeMatchingTable(input) {
    var ts = input.typical_size, wp = input.width_pref, fp = input.fit_pref, ft = input.fit_type;
    var hasWide = !!input.hasWide;
    var idealSize = ts, recWidth = 'M';

    if (ft === 'tts') {
      if (wp === 'narrow' || wp === 'medium') {
        if (fp === 'regular') { idealSize = ts; recWidth = 'M'; }
        else if (hasWide) { idealSize = ts; recWidth = 'W'; }
        else { idealSize = ts + 0.5; recWidth = 'M'; }
      } else if (wp === 'wide') {
        if (fp === 'regular') {
          if (hasWide) { idealSize = ts; recWidth = 'W'; }
          else { idealSize = ts + 0.5; recWidth = 'M'; }
        } else {
          if (hasWide) { idealSize = ts + 0.5; recWidth = 'W'; }
          else { idealSize = ts + 1.0; recWidth = 'M'; }
        }
      }
    } else if (ft === 'runs_small_half') {
      if (wp === 'narrow') {
        if (fp === 'regular') { idealSize = ts; recWidth = 'M'; }
        else { idealSize = ts + 0.5; recWidth = 'M'; }
      } else if (wp === 'medium') {
        if (fp === 'regular') {
          if (hasWide) { idealSize = ts; recWidth = 'W'; }
          else { idealSize = ts + 0.5; recWidth = 'M'; }
        } else {
          if (hasWide) { idealSize = ts + 0.5; recWidth = 'W'; }
          else { idealSize = ts + 0.5; recWidth = 'M'; }
        }
      } else if (wp === 'wide') {
        // Per Dima 2026-05-31 v3: runs_small_half + Wide foot — Regular and Relaxed
        // are now IDENTICAL (both use SIZE +0.5 W). Going +1.0 felt too aggressive for
        // Relaxed in runs-small shoes when half-size already compensates.
        if (hasWide) { idealSize = ts + 0.5; recWidth = 'W'; }
        else { idealSize = ts + 1.0; recWidth = 'M'; }
      }
    }
    return { size: idealSize, width: recWidth };
  }

  function recommendSize(input) {
    var sizesM = input.available_sizes_m || [];
    var sizesW = input.available_sizes_w || [];
    var hasWideGlobal = sizesW.length > 0;

    var ideal = computeMatchingTable({
      typical_size: input.typical_size,
      width_pref: input.width_pref,
      fit_pref: input.fit_pref,
      fit_type: input.fit_type,
      hasWide: true
    });

    var initial = computeMatchingTable({
      typical_size: input.typical_size,
      width_pref: input.width_pref,
      fit_pref: input.fit_pref,
      fit_type: input.fit_type,
      hasWide: hasWideGlobal
    });

    if (initial.width === 'M') {
      var clampedM = clampToAvailableSizes(initial.size, sizesM);
      return { size: clampedM.size, width: 'M', was_clamped: clampedM.was_clamped, clamp_reason: clampedM.reason, ideal_size: ideal.size, ideal_width: ideal.width };
    }

    if (sizesW.indexOf(initial.size) !== -1) {
      return { size: initial.size, width: 'W', was_clamped: false, clamp_reason: null, ideal_size: ideal.size, ideal_width: ideal.width };
    }
    // Per Dima 2026-06-02: do NOT round-up within the Wide pool. The matching table's
    // "(else SIZE +X in Medium)" branch is the ONLY fallback when exact (size, W) doesn't
    // exist — whether the size is missing due to half-size gap, range, or anything else.
    var fallback = computeMatchingTable({
      typical_size: input.typical_size,
      width_pref: input.width_pref,
      fit_pref: input.fit_pref,
      fit_type: input.fit_type,
      hasWide: false
    });
    var clampedMFallback = clampToAvailableSizes(fallback.size, sizesM);
    return { size: clampedMFallback.size, width: 'M', was_clamped: true, clamp_reason: clampedMFallback.reason || 'no_wide_at_size', ideal_size: ideal.size, ideal_width: ideal.width };
  }

  // ========== STORAGE (per-style) ==========
  function profileKey(productHandle) {
    return STORAGE_KEY_PREFIX + (productHandle || 'default');
  }
  function loadProfile(productHandle) {
    try {
      var raw = localStorage.getItem(profileKey(productHandle));
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    // Fallback: customer metafield (if available and contains this handle)
    if (window.xenaCustomerFitProfile && window.xenaCustomerFitProfile[productHandle]) {
      return window.xenaCustomerFitProfile[productHandle];
    }
    return null;
  }
  function saveProfile(productHandle, profile) {
    try {
      profile.updated_at = new Date().toISOString();
      localStorage.setItem(profileKey(productHandle), JSON.stringify(profile));
    } catch (e) {}
  }
  function clearProfile(productHandle) {
    try { localStorage.removeItem(profileKey(productHandle)); } catch (e) {}
  }
  function getSessionId() {
    try {
      var sid = sessionStorage.getItem(SESSION_ID_KEY);
      if (sid) return sid;
      var d = new Date();
      var ymd = '' + d.getFullYear() + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      sid = 'ff_' + ymd + '_' + Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem(SESSION_ID_KEY, sid);
      return sid;
    } catch (e) { return 'ff_unknown'; }
  }

  // ========== EVENTS ==========
  function fireKlaviyoEvent(eventName, payload) {
    var now = Date.now();
    if (eventName === 'Fit Finder Started' && now - lastKlaviyoEvent < KLAVIYO_THROTTLE_MS) return;
    lastKlaviyoEvent = now;
    try {
      if (window._learnq) window._learnq.push(['track', eventName, payload]);
      else if (window.klaviyo && typeof window.klaviyo.push === 'function') window.klaviyo.push(['track', eventName, payload]);
    } catch (e) {}
  }
  function fireGAEvent(eventName, payload) {
    try {
      if (typeof gtag === 'function') gtag('event', eventName, payload || {});
      else if (window.dataLayer) window.dataLayer.push({ event: eventName, ff: payload });
    } catch (e) {}
  }

  function isDebugMode() { return (window.location.search || '').indexOf('fitfinder=debug') !== -1; }
  function debugLog(state) {
    if (!isDebugMode()) return;
    var panel = document.querySelector('[data-fit-finder-debug]');
    var pre = document.querySelector('[data-fit-finder-debug-content]');
    if (panel) panel.hidden = false;
    if (pre) pre.textContent = JSON.stringify(state, null, 2);
  }

  // ========== STATE ==========
  var state = {
    productContext: null,
    profile: { typical_size: null, width_pref: null, fit_pref: null },
    currentStep: 1,
    triggerEl: null,
    lastRecommendation: null,
  };

  function readProductContext(triggerEl) {
    function parseJSON(str, fallback) { try { return JSON.parse(str); } catch (e) { return fallback; } }
    return {
      product_id: triggerEl.dataset.ffProductId,
      product_handle: triggerEl.dataset.ffProductHandle,
      product_title: triggerEl.dataset.ffProductTitle,
      style_name: triggerEl.dataset.ffStyleName,
      fit_type: triggerEl.dataset.ffFitType,
      available_sizes_m: parseJSON(triggerEl.dataset.ffSizesM, []).map(parseFloat).filter(function (n) { return !isNaN(n); }).sort(function (a, b) { return a - b; }),
      available_sizes_w: parseJSON(triggerEl.dataset.ffSizesW, []).map(parseFloat).filter(function (n) { return !isNaN(n); }).sort(function (a, b) { return a - b; }),
    };
  }

  // ========== UI ==========
  function showStep(stepNum) {
    state.currentStep = stepNum;
    document.querySelectorAll('[data-fit-finder-screen]').forEach(function (el) { el.hidden = true; });
    var target = document.querySelector('[data-fit-finder-screen="step-' + stepNum + '"]');
    if (target) target.hidden = false;
    document.querySelectorAll('.fit-finder__progress-step').forEach(function (el) {
      var n = parseInt(el.dataset.step, 10);
      el.classList.remove('is-active', 'is-done');
      if (n < stepNum) el.classList.add('is-done');
      else if (n === stepNum) el.classList.add('is-active');
    });
    var indicator = document.querySelector('[data-fit-finder-step-indicator]');
    if (indicator) indicator.textContent = 'Step ' + stepNum + ' of 3';
  }

  function showResult() {
    document.querySelectorAll('[data-fit-finder-screen]').forEach(function (el) { el.hidden = true; });

    var rec = recommendSize({
      typical_size: state.profile.typical_size,
      width_pref: state.profile.width_pref,
      fit_pref: state.profile.fit_pref,
      fit_type: state.productContext.fit_type,
      available_sizes_m: state.productContext.available_sizes_m,
      available_sizes_w: state.productContext.available_sizes_w,
    });
    state.lastRecommendation = rec;

    // Route to 'not available' when recommendation can't be filled (max/min/empty pool).
    var notAvailable = (rec.clamp_reason === 'max_size' || rec.clamp_reason === 'min_size' || rec.clamp_reason === 'empty_pool');
    if (notAvailable) {
      var naScreen = document.querySelector('[data-fit-finder-screen="not-available"]');
      if (naScreen) naScreen.hidden = false;
      var naSizeStr = String(rec.ideal_size);
      if (rec.ideal_width === 'W') naSizeStr += 'W';
      var naSizeEl = document.querySelector('[data-fit-finder-na-size]');
      var naStyleEl = document.querySelector('[data-fit-finder-na-style]');
      if (naSizeEl) naSizeEl.textContent = naSizeStr;
      if (naStyleEl) naStyleEl.textContent = state.productContext.style_name || state.productContext.product_title || '';
      var naPayload = {
        outcome: 'not_available',
        typical_size: state.profile.typical_size, width_pref: state.profile.width_pref, fit_pref: state.profile.fit_pref,
        ideal_size: rec.ideal_size, ideal_width: rec.ideal_width,
        clamp_reason: rec.clamp_reason,
        fit_type: state.productContext.fit_type, product_id: state.productContext.product_id, product_handle: state.productContext.product_handle,
        session_id: getSessionId(), ff_version: FF_VERSION
      };
      fireKlaviyoEvent('Fit Finder Completed', naPayload);
      fireGAEvent('fit_finder_completed', { fit_type: naPayload.fit_type, outcome: 'not_available' });
      return;
    }

    var result = document.querySelector('[data-fit-finder-screen="result"]');
    if (result) result.hidden = false;

    var sizeStr = String(rec.size);
    if (rec.width === 'W') sizeStr += 'W';
    var sizeEl = document.querySelector('[data-fit-finder-result-size]');
    var styleEl = document.querySelector('[data-fit-finder-result-style]');
    if (sizeEl) sizeEl.textContent = sizeStr;
    if (styleEl) styleEl.textContent = state.productContext.style_name || state.productContext.product_title || '';

    var clampEl = document.querySelector('[data-fit-finder-result-clamp]');
    if (clampEl) {
      if (rec.clamp_reason === 'max_size') {
        clampEl.textContent = state.productContext.style_name + ' is offered up to size ' + rec.size + ' \u2014 this is our closest available fit.';
        clampEl.hidden = false;
      } else if (rec.clamp_reason === 'min_size') {
        clampEl.textContent = state.productContext.style_name + ' starts at size ' + rec.size + ' \u2014 this is our closest available fit.';
        clampEl.hidden = false;
      } else if (rec.clamp_reason === 'no_wide_at_size') {
        clampEl.textContent = 'Wide isn\u2019t offered at that size in ' + state.productContext.style_name + ' \u2014 here\u2019s our closest match.';
        clampEl.hidden = false;
      } else {
        clampEl.hidden = true;
      }
    }

    debugLog({ profile: state.profile, productContext: state.productContext, recommendation: rec });

    var customerEmail = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page && window.ShopifyAnalytics.meta.page.customerEmail) || null;
    var payload = {
      typical_size: state.profile.typical_size, width_pref: state.profile.width_pref, fit_pref: state.profile.fit_pref,
      recommendation_size: rec.size, recommendation_width: rec.width,
      fit_type: state.productContext.fit_type,
      product_id: state.productContext.product_id, product_handle: state.productContext.product_handle, product_title: state.productContext.product_title,
      was_clamped: rec.was_clamped, clamp_reason: rec.clamp_reason,
      session_id: getSessionId(), ff_version: FF_VERSION,
    };
    if (customerEmail) payload.$email = customerEmail;
    fireKlaviyoEvent('Fit Finder Completed', payload);
    fireGAEvent('fit_finder_completed', { fit_type: payload.fit_type, was_clamped: payload.was_clamped });
  }

  function renderSizeGrid() {
    var grid = document.querySelector('[data-fit-finder-size-grid]');
    if (!grid) return;
    grid.innerHTML = '';
    ALL_SIZES.forEach(function (size) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'fit-finder__size-btn';
      btn.setAttribute('data-fit-finder-size', size);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.textContent = size;
      grid.appendChild(btn);
    });
  }

  // ========== FOCUS TRAP (optimization #9) ==========
  var lastFocused = null;
  function getFocusable(modal) {
    return modal.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
  }
  function trapTab(e) {
    if (e.key !== 'Tab') return;
    var modal = document.getElementById('fit-finder-modal');
    if (!modal || modal.classList.contains('fit-finder--hidden')) return;
    var focusables = getFocusable(modal);
    if (!focusables.length) return;
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
  }

  function openModal(triggerEl) {
    lastFocused = document.activeElement;
    state.triggerEl = triggerEl;
    state.productContext = readProductContext(triggerEl);
    state.profile = loadProfile(state.productContext.product_handle) || { typical_size: null, width_pref: null, fit_pref: null };

    var modal = document.getElementById('fit-finder-modal');
    if (!modal) return;
    modal.classList.remove('fit-finder--hidden');
    modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    if (state.profile.typical_size && state.profile.width_pref && state.profile.fit_pref) {
      showResult();
    } else {
      renderSizeGrid();
      showStep(1);
    }

    // Focus first focusable
    setTimeout(function () {
      var focusables = getFocusable(modal);
      if (focusables.length) focusables[0].focus();
    }, 50);

    fireKlaviyoEvent('Fit Finder Started', { product_id: state.productContext.product_id, session_id: getSessionId() });
    fireGAEvent('fit_finder_started', {});
  }

  function closeModal() {
    var modal = document.getElementById('fit-finder-modal');
    if (!modal) return;
    modal.classList.add('fit-finder--hidden');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function startOver() {
    state.profile = { typical_size: null, width_pref: null, fit_pref: null };
    clearProfile(state.productContext.product_handle);
    renderSizeGrid();
    showStep(1);
  }

  // ========== VARIANT PICKER PRE-SELECT ==========
  function clickOptionValue(fieldset, value) {
    var btns = fieldset.querySelectorAll('input[type="radio"], button[role="option"], [data-option-value]');
    for (var i = 0; i < btns.length; i++) {
      var b = btns[i];
      var v = b.value || b.getAttribute('value') || b.textContent.trim();
      if (v === value || String(v) === String(value)) {
        if (b.click) b.click();
        return true;
      }
    }
    return false;
  }
  function getCurrentColorFromPicker() {
    var picker = document.querySelector('variant-picker');
    if (!picker) return null;
    var fieldsets = picker.querySelectorAll('.variant-picker__option');
    for (var i = 0; i < fieldsets.length; i++) {
      var legend = fieldsets[i].querySelector('legend');
      if (!legend) continue;
      if (legend.textContent.trim().toLowerCase().replace(':', '') === 'color') {
        var sel = fieldsets[i].querySelector('input:checked, [aria-selected="true"], .is-selected');
        if (sel) return sel.value || sel.getAttribute('value') || sel.textContent.trim();
      }
    }
    return null;
  }
  function preSelectVariant(recommendation) {
    var handle = state.productContext && state.productContext.product_handle;
    if (!handle) return false;
    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (productData) {
        var variants = productData.variants || [];
        var optionNames = (productData.options || []).map(function (o) {
          return (typeof o === 'string') ? o : (o && o.name) || '';
        });
        var sizeIdx = -1, widthIdx = -1, colorIdx = -1;
        optionNames.forEach(function (n, i) {
          var lower = (n || '').toLowerCase();
          if (lower === 'size') sizeIdx = i;
          else if (lower === 'width') widthIdx = i;
          else if (lower === 'color') colorIdx = i;
        });
        var sizeStr = String(recommendation.size);
        var widthLabel = recommendation.width === 'W' ? 'Wide' : 'Regular';
        var currentColor = getCurrentColorFromPicker();
        var matches = variants.filter(function (v) {
          if (!v.available) return false;
          if (sizeIdx >= 0 && String(v.options[sizeIdx]) !== sizeStr) return false;
          if (widthIdx >= 0 && v.options[widthIdx] !== widthLabel) return false;
          return true;
        });
        if (matches.length === 0) return;
        var target = null;
        if (currentColor && colorIdx >= 0) {
          target = matches.find(function (v) { return v.options[colorIdx] === currentColor; });
        }
        if (!target) target = matches[0];
        var picker = document.querySelector('variant-picker');
        if (!picker) return;
        var fieldsets = picker.querySelectorAll('.variant-picker__option');
        optionNames.forEach(function (optName, idx) {
          if (!optName) return;
          var value = target.options[idx];
          for (var i = 0; i < fieldsets.length; i++) {
            var legend = fieldsets[i].querySelector('legend');
            if (!legend) continue;
            if (legend.textContent.trim().toLowerCase().replace(':', '') === optName.toLowerCase()) {
              clickOptionValue(fieldsets[i], value);
              break;
            }
          }
        });
      })
      .catch(function () {});
    return true;
  }

  function attachLineItemProperties(productForm, profile, recommendation, ctx) {
    if (!productForm) return;
    var props = {
      '_ff_typical_size': profile.typical_size,
      '_ff_width': profile.width_pref,
      '_ff_fit': profile.fit_pref,
      '_ff_rec_size': recommendation.size,
      '_ff_rec_width': recommendation.width,
      '_ff_fit_type': ctx.fit_type,
      '_ff_sizes_m_count': (ctx.available_sizes_m || []).length,
      '_ff_sizes_w_count': (ctx.available_sizes_w || []).length,
      '_ff_clamped': recommendation.was_clamped ? 'true' : 'false',
      '_ff_clamp_reason': recommendation.clamp_reason || '',
      '_ff_session_id': getSessionId(),
      '_ff_version': FF_VERSION,
    };
    Object.keys(props).forEach(function (key) {
      var existing = productForm.querySelector('input[name="properties[' + key + ']"]');
      if (existing) existing.value = String(props[key]);
      else {
        var input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'properties[' + key + ']';
        input.value = String(props[key]);
        productForm.appendChild(input);
      }
    });
  }

  function findProductForm() {
    // v1.3: prefer the theme's real buy form; a second /cart/add form (app-injected) precedes it in DOM order.
    return document.querySelector('form[is="product-form"][action*="/cart/add"]')
        || document.querySelector('form[action*="/cart/add"]')
        || document.querySelector('product-form form')
        || document.querySelector('form.shopify-product-form');
  }

  function renderSavedPill(recommendation) {
    var sizeFieldset = null;
    document.querySelectorAll('.variant-picker__option').forEach(function (fs) {
      var legend = fs.querySelector('legend');
      if (legend && legend.textContent.trim().toLowerCase().replace(':', '') === 'size') sizeFieldset = fs;
    });
    if (!sizeFieldset) return;
    var existing = sizeFieldset.parentNode.querySelector('.fit-finder-saved-pill');
    if (existing) existing.remove();
    var sizeStr = String(recommendation.size);
    if (recommendation.width === 'W') sizeStr += 'W';
    var pill = document.createElement('div');
    pill.className = 'fit-finder-saved-pill';
    pill.innerHTML = 'Size ' + sizeStr + ' selected from your Find My Size profile' +
      ' <button type="button" class="fit-finder-saved-pill__change" data-fit-finder-trigger-saved>Change</button>';
    sizeFieldset.parentNode.insertBefore(pill, sizeFieldset);
    pill.querySelector('[data-fit-finder-trigger-saved]').addEventListener('click', function () {
      var trigger = document.querySelector('[data-fit-finder-trigger]');
      if (trigger) openModal(trigger);
    });
  }

  // ========== AUTO-APPLY ON PDP LOAD ==========
  function autoApplyOnLoad() {
    var trigger = document.querySelector('[data-fit-finder-trigger]');
    if (!trigger) return;
    var ctx = readProductContext(trigger);
    var profile = loadProfile(ctx.product_handle);
    if (!profile || !profile.typical_size || !profile.width_pref || !profile.fit_pref) return;
    var rec = recommendSize({
      typical_size: profile.typical_size, width_pref: profile.width_pref, fit_pref: profile.fit_pref,
      fit_type: ctx.fit_type, available_sizes_m: ctx.available_sizes_m, available_sizes_w: ctx.available_sizes_w,
    });
    state.productContext = ctx; state.profile = profile; state.lastRecommendation = rec;
    setTimeout(function () {
      preSelectVariant(rec);
      renderSavedPill(rec);
      var form = findProductForm();
      if (form) attachLineItemProperties(form, profile, rec, ctx);
    }, 300);
  }

  // ========== CART ATTRIBUTE SYNC (customer metafield write via Shopify Flow) ==========
  // Writes the per-style profile to cart attributes via /cart/update.js.
  // Shopify ties carts to logged-in customers across devices, so the profile
  // persists cross-device through the cart. A Shopify Flow trigger on Order
  // creation then syncs cart attributes -> customer.metafields.xena.fit_profile.
  function writeProfileToCart(productHandle, profile) {
    if (!profile || !productHandle) return;
    var attrs = {};
    attrs['_xena_ff_' + productHandle] = JSON.stringify({
      ts: profile.typical_size,
      w: profile.width_pref,
      f: profile.fit_pref,
      u: profile.updated_at,
      v: FF_VERSION
    });
    attrs['_xena_ff_session_id'] = getSessionId();
    try {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).catch(function () {});
    } catch (e) {}
  }

  // On page load, fetch /cart.js and hydrate any saved profiles into localStorage.
  // Gives cross-device sync via the customer's cart for logged-in users.
  function hydrateProfilesFromCart() {
    try {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          if (!cart || !cart.attributes) return;
          Object.keys(cart.attributes).forEach(function (key) {
            if (key.indexOf('_xena_ff_') !== 0) return;
            if (key === '_xena_ff_session_id') return;
            var handle = key.replace('_xena_ff_', '');
            try {
              var compact = JSON.parse(cart.attributes[key]);
              if (!localStorage.getItem(profileKey(handle))) {
                var expanded = {
                  typical_size: compact.ts,
                  width_pref: compact.w,
                  fit_pref: compact.f,
                  updated_at: compact.u
                };
                localStorage.setItem(profileKey(handle), JSON.stringify(expanded));
              }
            } catch (e) {}
          });
          // After hydration, re-run auto-apply in case current PDP profile just appeared
          autoApplyOnLoad();
        })
        .catch(function () {});
    } catch (e) {}
  }

  // ========== ATC PROPERTY INJECTION + ENRICHMENT (v1.2, 2026-09-02) ==========
  // ROOT-CAUSE FIX: the theme's <form is="product-form"> builds its FormData
  // inside its own submit listener, and the buy area re-renders on variant
  // change (which preSelectVariant() itself triggers), wiping pre-injected
  // hidden inputs and form-bound listeners. Result: the _ff_* line-item
  // properties (and 'Fit Finder Added to Cart') never reached any order.
  // Fix: inject the properties at SUBMIT time, in the CAPTURE phase (runs
  // before the form's own listener serializes FormData), on the actual
  // submitting form (e.target) - immune to re-renders and to multiple
  // /cart/add forms on the page.
  function isFFTargetForm(form) {
    if (!form || !form.getAttribute || !state.productContext) return false;
    if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return false;
    var pid = String(state.productContext.product_id || '');
    // v1.3 (2026-09-23): form.id is SHADOWED by the form's own <input name="id"> (returns the input element,
    // not the id string), so form.id.indexOf threw a TypeError inside the capture listener on every submit
    // and the _ff_* properties + 'Fit Finder Added to Cart' never fired. Read the attribute instead.
    var fid = form.getAttribute('id') || '';
    if (pid && fid.indexOf('product-form-' + pid) === 0) return true;
    // Never tag quick-buy submits for a different product
    if (form.closest && form.closest('.quick-buy-modal__content')) return false;
    var h = state.productContext.product_handle;
    return !!(h && window.location.pathname.indexOf('/products/' + h) !== -1);
  }
  function hookProductForm() {
    if (document.__ffSubmitHooked) return;
    document.__ffSubmitHooked = true;
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isFFTargetForm(form)) return;
      if (!(state.profile && state.profile.typical_size && state.lastRecommendation)) return;
      attachLineItemProperties(form, state.profile, state.lastRecommendation, state.productContext);
      fireKlaviyoEvent('Fit Finder Added to Cart', {
        product_id: state.productContext.product_id,
        product_handle: state.productContext.product_handle,
        recommendation_size: state.lastRecommendation.size,
        recommendation_width: state.lastRecommendation.width,
        typical_size: state.profile.typical_size,
        width_pref: state.profile.width_pref,
        fit_pref: state.profile.fit_pref,
        session_id: getSessionId(),
      });
    }, true);
  }

  // ========== EVENT BINDING ==========
  function bindEvents() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-fit-finder-trigger]');
      if (trigger) { e.preventDefault(); openModal(trigger); return; }
      if (e.target.closest('[data-fit-finder-close]')) { closeModal(); return; }

      var sizeBtn = e.target.closest('[data-fit-finder-size]');
      if (sizeBtn) {
        state.profile.typical_size = parseFloat(sizeBtn.dataset.fitFinderSize);
        document.querySelectorAll('[data-fit-finder-size]').forEach(function (b) { b.classList.remove('is-selected'); b.setAttribute('aria-checked', 'false'); });
        sizeBtn.classList.add('is-selected'); sizeBtn.setAttribute('aria-checked', 'true');
        fireKlaviyoEvent('Fit Finder Step Completed', { step: 1, value: state.profile.typical_size, session_id: getSessionId() });
        setTimeout(function () { showStep(2); }, 200);
        return;
      }
      var widthBtn = e.target.closest('[data-fit-finder-width]');
      if (widthBtn) {
        state.profile.width_pref = widthBtn.dataset.fitFinderWidth;
        document.querySelectorAll('[data-fit-finder-width]').forEach(function (b) { b.classList.remove('is-selected'); });
        widthBtn.classList.add('is-selected');
        fireKlaviyoEvent('Fit Finder Step Completed', { step: 2, value: state.profile.width_pref, session_id: getSessionId() });
        setTimeout(function () { showStep(3); }, 200);
        return;
      }
      var fitBtn = e.target.closest('[data-fit-finder-fit]');
      if (fitBtn) {
        state.profile.fit_pref = fitBtn.dataset.fitFinderFit;
        document.querySelectorAll('[data-fit-finder-fit]').forEach(function (b) { b.classList.remove('is-selected'); });
        fitBtn.classList.add('is-selected');
        fireKlaviyoEvent('Fit Finder Step Completed', { step: 3, value: state.profile.fit_pref, session_id: getSessionId() });
        saveProfile(state.productContext.product_handle, state.profile);
        writeProfileToCart(state.productContext.product_handle, state.profile);
        setTimeout(function () { showResult(); }, 250);
        return;
      }
      if (e.target.closest('[data-fit-finder-select-size]')) {
        if (state.lastRecommendation) {
          preSelectVariant(state.lastRecommendation);
          renderSavedPill(state.lastRecommendation);
          var form = findProductForm();
          if (form) attachLineItemProperties(form, state.profile, state.lastRecommendation, state.productContext);
          // Safety-net cart attribute write — re-writes profile even if the step-3 write failed/raced.
          // The Flow's condition checks order.customAttributes for _xena_ff_<handle> prefix.
          writeProfileToCart(state.productContext.product_handle, state.profile);
        }
        closeModal();
        var atc = document.querySelector('[data-fit-finder-trigger]');
        if (atc) atc.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (e.target.closest('[data-fit-finder-start-over]')) { startOver(); return; }

      if (e.target.closest('[data-fit-finder-select-new-size]')) {
        closeModal();
        return;
      }
      if (e.target.closest('[data-fit-finder-select-new-style]')) {
        closeModal();
        window.location.href = '/collections/all-products';
        return;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var modal = document.getElementById('fit-finder-modal');
        if (modal && !modal.classList.contains('fit-finder--hidden')) closeModal();
      }
      trapTab(e);
    });
  }

  // NOTE (Dex, 2026-07-23): Auto-open via ?fitfinder=open depends entirely on a
  // [data-fit-finder-trigger] element existing on the page (rendered by
  // snippets/fit-finder-button.liquid, gated on product.metafields.xena.fit_type).
  // If that selector/markup is ever renamed or removed in a future theme edit,
  // this silently no-ops -- no console error, no visible failure. Deep links from
  // the Order Confirmation email (email_templates/order_confirmation) will still
  // land on the correct PDP, they just won't auto-launch the modal. If Fit Finder
  // triggers are ever restructured, grep the theme for 'fitfinder=open' and
  // 'data-fit-finder-trigger' together before shipping.
  function checkAutoOpenParam() {
    try {
      var params = new URLSearchParams(window.location.search);
      if (params.get('fitfinder') !== 'open') return;
      // Poll instead of a fixed delay -- on slower devices/connections the trigger
      // button may not be in the DOM at a fixed mark (previously a flat 400ms
      // setTimeout, which could race the button's render). Poll every 100ms for up
      // to ~3s, then give up silently.
      var attempts = 0;
      var maxAttempts = 30;
      var pollTimer = setInterval(function () {
        attempts++;
        var trigger = document.querySelector('[data-fit-finder-trigger]');
        if (trigger) {
          clearInterval(pollTimer);
          openModal(trigger);
        } else if (attempts >= maxAttempts) {
          clearInterval(pollTimer);
        }
      }, 100);
    } catch (e) {}
  }

  function init() {
    bindEvents();
    autoApplyOnLoad();
    hookProductForm();
    hydrateProfilesFromCart();
    checkAutoOpenParam();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.XenaFitFinder = { recommendSize: recommendSize, loadProfile: loadProfile, clearProfile: clearProfile, state: state };
})();